/* eslint-disable react/display-name */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Paper, FormControl, InputBase } from '@material-ui/core';
import NumberFormat from 'react-number-format';
import {
  fontColors,
  backgroundColors,
  colorDanger,
} from '../../material-ui-theme';
import FormAdvancedLabel from './FormAdvancedLabel';

// Form value pickup
// component: 'numeric-input'
// value: value

function NumberFormatCustom(props) {
  const { inputRef, onChange, allowNegative, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            value: values.floatValue,
          },
        });
      }}
      allowNegative={allowNegative}
      isNumericString
    />
  );
}

const useStyles = makeStyles((theme) => ({
  inputRoot: {
    width: '100%',
    color: 'var(--color-fontColors-black)',
  },
  inputLabelRoot: {
    marginTop: theme.spacing(3),
    width: '100%',
  },
  inputPaper: {
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '& input': {
      padding: '2px 4px',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingTop: `${theme.spacing(1) + 0.5}px`,
      paddingBottom: `${theme.spacing(1) + 0.5}px`,
      fontSize: '0.9rem',
      fontWeight: 400,
      color: 'var(--color-fontColors-black)',
    },
  },
  inputPaperDisabled: {
    backgroundColor: backgroundColors.disabled,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '& input': {
      padding: '2px 4px',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingTop: `${theme.spacing(1) + 0.5}px`,
      paddingBottom: `${theme.spacing(1) + 0.5}px`,
      fontSize: '0.9rem',
      fontWeight: 400,
      color: fontColors.disabled,
      '-webkit-text-fill-color': fontColors.disabled,
      opacity: 1 /* required on iOS */,
    },
    '& [data-hidevalueondisabled]': {
      color: 'transparent',
      '-webkit-text-fill-color': 'transparent',
    },
  },
  input: {
    flex: 1,
  },
  helperText: {
    margin: 0,
    fontSize: '0.9rem',
    marginTop: '3px',
    textAlign: 'left',
    fontWeight: 500,
    color: colorDanger.main,
    paddingLeft: theme.spacing(1),
  },
}));

const ElevatedNumericInput = memo(
  ({
    label,
    onChange,
    error,
    helperText,
    disabled,
    decimalScale,
    suffix,
    prefix,
    hideValueOnDisabled,
    allowNegative,
    ...restProps
  }) => {
    const classes = useStyles();
    const inputProps = {
      decimalScale,
      'data-component': 'numeric-input',
    };

    if (suffix !== '') {
      inputProps.suffix = suffix;
      inputProps['data-suffix'] = suffix;
    }

    if (prefix !== '') {
      inputProps.prefix = prefix;
      inputProps['data-prefix'] = prefix;
    }

    if (hideValueOnDisabled) {
      inputProps['data-hidevalueondisabled'] = true;
    }

    inputProps.allowNegative = allowNegative;

    return (
      <FormControl
        className={label ? classes.inputLabelRoot : classes.inputRoot}
        error={error}
      >
        {label && label !== '' ? (
          <FormAdvancedLabel>{label}</FormAdvancedLabel>
        ) : null}
        <Paper
          className={
            disabled
              ? `${classes.inputPaperDisabled} inputPaperDisabled-class`
              : classes.inputPaper
          }
          elevation={2}
        >
          <InputBase
            onChange={(event) => onChange(event.target.value)}
            className={classes.input}
            inputComponent={NumberFormatCustom}
            inputProps={inputProps}
            disabled={disabled}
            {...restProps}
          />
        </Paper>
        {error && helperText !== '' ? (
          <p className={classes.helperText}>{helperText}</p>
        ) : null}
      </FormControl>
    );
  }
);

ElevatedNumericInput.defaultProps = {
  label: '',
  onChange: () => {},
  error: false,
  helperText: '',
  disabled: false,
  decimalScale: 0,
  suffix: '',
  prefix: '',
  hideValueOnDisabled: false,
  allowNegative: false,
};

ElevatedNumericInput.propTypes = {
  label: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  decimalScale: PropTypes.number,
  suffix: PropTypes.string,
  prefix: PropTypes.string,
  hideValueOnDisabled: PropTypes.bool,
  allowNegative: PropTypes.bool,
};

export default ElevatedNumericInput;
